import { Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  CardContent,
  Divider,
  FormHelperText,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Formik } from 'formik';
import React, { useState } from 'react';
import { INDUSTRY_OPTIONS } from '../../static-data/DropdownOptions';
import { useNotification } from '../../hooks/NotificationContext';
import { editCurrentUser, updateOrganization } from '../../modules/users';
import CountryPhoneInput from '../elements/common/CountryPhoneInput';
import CustomIconButton from '../elements/CustomIconButton';
import InputField from '../elements/InputField';
import Modal from '../elements/Modal';

const SectionLabel = ({ children }) => (
  <Grid item xs={12}>
    <Typography variant="overline" color="text.secondary">
      {children}
    </Typography>
    <Divider sx={{ mt: 0.5 }} />
  </Grid>
);

const PersonalForm = ({
  values,
  errors,
  touched,
  handleBlur,
  handleChange,
  setFieldValue,
}) => (
  <Grid container spacing={1.5}>
    <SectionLabel>Personal Information</SectionLabel>

    <Grid item xs={12} sm={6}>
      <InputField
        label="First Name"
        id="first_name"
        name="first_name"
        fullWidth
        placeholder="John"
        value={values.first_name}
        error={Boolean(touched.first_name && errors.first_name)}
        helperText={touched.first_name && errors.first_name}
        onBlur={handleBlur}
        onChange={handleChange}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <InputField
        label="Last Name"
        id="last_name"
        name="last_name"
        fullWidth
        placeholder="Doe"
        value={values.last_name}
        error={Boolean(touched.last_name && errors.last_name)}
        helperText={touched.last_name && errors.last_name}
        onBlur={handleBlur}
        onChange={handleChange}
      />
    </Grid>

    <Grid item xs={12}>
      <CountryPhoneInput
        countryCode={values.country_code}
        phoneNumber={values.phone_number}
        label="Phone Number"
        onChange={(field, value) => {
          if (field === 'countryCode') setFieldValue('country_code', value);
          else if (field === 'phoneNumber')
            setFieldValue('phone_number', value);
        }}
      />
    </Grid>
  </Grid>
);

const OrgForm = ({
  values,
  errors,
  touched,
  handleBlur,
  handleChange,
  setFieldValue,
}) => {
  const industryError = Boolean(touched.industry && errors.industry);

  return (
    <Grid container spacing={1.5}>
      <SectionLabel>Organization Details</SectionLabel>
      <Grid item xs={12}>
        <InputField
          label="Organization Name"
          id="organization_name"
          name="organization_name"
          fullWidth
          placeholder="Acme Corp"
          value={values.organization_name}
          error={Boolean(touched.organization_name && errors.organization_name)}
          helperText={touched.organization_name && errors.organization_name}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box>
          <Typography
            variant="body2"
            component="label"
            htmlFor="industry"
            sx={{
              display: 'block',
              mb: 0.75,
              fontSize: '0.8125rem',
              fontFamily: 'inherit',
            }}>
            Industry
          </Typography>

          <Select
            id="industry"
            name="industry"
            value={values.industry}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
            displayEmpty
            error={industryError}
            input={<OutlinedInput />}
            MenuProps={{ PaperProps: { sx: { maxHeight: 260 } } }}>
            <MenuItem value="" disabled>
              <Typography color="text.disabled">Select an industry</Typography>
            </MenuItem>
            {INDUSTRY_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>

          {industryError && (
            <FormHelperText error sx={{ mx: '14px' }}>
              {errors.industry}
            </FormHelperText>
          )}
        </Box>

        {/* ↓ Shows when "Other" is selected — pre-filled if custom value was saved */}
        {values.industry === 'Other' && (
          <Box sx={{ mt: 2 }}>
            <InputField
              label="Please specify"
              id="industry_other"
              name="industry_other"
              fullWidth
              placeholder="e.g. Logistics, Legal, Agriculture"
              value={values.industry_other}
              error={Boolean(touched.industry_other && errors.industry_other)}
              helperText={touched.industry_other && errors.industry_other}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </Box>
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <CountryPhoneInput
          countryCode={values.org_country_code}
          phoneNumber={values.org_phone_number}
          label="Phone Number"
          onChange={(field, value) => {
            if (field === 'countryCode')
              setFieldValue('org_country_code', value);
            else if (field === 'phoneNumber')
              setFieldValue('org_phone_number', value);
          }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <InputField
          label="Website"
          id="website"
          name="website"
          type="url"
          fullWidth
          placeholder="https://yourcompany.com"
          value={values.website}
          error={Boolean(touched.website && errors.website)}
          helperText={touched.website && errors.website}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <InputField
          label="LinkedIn Page URL"
          id="linkedin"
          name="linkedin"
          type="url"
          fullWidth
          placeholder="https://linkedin.com/company/acme"
          value={values.linkedin}
          error={Boolean(touched.linkedin && errors.linkedin)}
          helperText={
            touched.linkedin && errors.linkedin ? errors.linkedin : 'Optional'
          }
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </Grid>

      <Grid item xs={12}>
        <InputField
          label="Why GAiNSS?"
          id="evaluation_reason"
          name="evaluation_reason"
          fullWidth
          multiline
          rows={3}
          placeholder="Describe the challenges you face, your current test process, and what you hope to achieve"
          value={values.evaluation_reason}
          error={Boolean(touched.evaluation_reason && errors.evaluation_reason)}
          helperText={
            touched.evaluation_reason && errors.evaluation_reason
              ? errors.evaluation_reason
              : `${values.evaluation_reason.length} / 1000  —  Optional`
          }
          onBlur={handleBlur}
          onChange={handleChange}
          inputProps={{ maxLength: 1000 }}
        />
      </Grid>
    </Grid>
  );
};

const ProfileDetails = ({ user, fetchUserData }) => {
  const isOrgAdmin = user?.role === 'org-admin';
  const { addNotification } = useNotification();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const savedIndustry = user?.industry || '';
  const isCustomIndustry =
    savedIndustry !== '' && !INDUSTRY_OPTIONS.includes(savedIndustry);

  const initialValues = {
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone_number: user?.phone_number || '',
    country_code: user?.country_code || 'IN',
    organization_name: user?.organization_name || '',
    org_phone_number: user?.org_phone_number || '',
    org_country_code: user?.org_country_code || 'IN',
    industry: isCustomIndustry ? 'Other' : savedIndustry,
    industry_other: isCustomIndustry ? savedIndustry : '',
    website: user?.website || '',
    linkedin: user?.linkedin || '',
    evaluation_reason: user?.evaluation_reason || '',
  };

  const handleClose = (resetForm) => {
    setOpen(false);
    setActiveTab(0);
    resetForm();
  };

  const handleSave = async (values, { setSubmitting, resetForm }) => {
    try {
      await editCurrentUser({
        first_name: values.first_name,
        last_name: values.last_name,
        phone_number: values.phone_number,
        country_code: values.country_code,
      });

      if (isOrgAdmin) {
        await updateOrganization({
          organization_name: values.organization_name,
          industry:
            values.industry === 'Other'
              ? values.industry_other
              : values.industry,
          org_phone_number: values.org_phone_number || null,
          org_country_code: values.org_country_code || null,
          website: values.website || null,
          linkedin: values.linkedin || null,
          evaluation_reason: values.evaluation_reason || null,
        });
      }

      addNotification('success', 'Profile updated successfully');
      fetchUserData();
      handleClose(resetForm);
    } catch (error) {
      addNotification(
        'danger',
        error?.message || 'Error updating your data. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <CustomIconButton
        sx={{ position: 'absolute', top: 10, right: 10 }}
        title="Edit details"
        disableRipple
        size="small"
        onClick={() => setOpen(true)}>
        <EditIcon />
      </CustomIconButton>

      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSave}>
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          resetForm,
          setFieldValue,
        }) => {
          const formProps = {
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            setFieldValue,
          };

          return (
            <Modal
              open={open}
              width="sm"
              modalTitle="Edit Profile"
              handleClose={() => handleClose(resetForm)}
              action={() => (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => handleClose(resetForm)}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              )}>
              <form noValidate onSubmit={handleSubmit}>
                <CardContent sx={{ pt: 0 }}>
                  {isOrgAdmin && (
                    <Tabs
                      value={activeTab}
                      onChange={(_, v) => setActiveTab(v)}
                      sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
                      <Tab label="Personal Info" />
                      <Tab label="Organization" />
                    </Tabs>
                  )}

                  <Box>
                    {activeTab === 0 ? (
                      <PersonalForm {...formProps} />
                    ) : (
                      <OrgForm {...formProps} />
                    )}
                  </Box>
                </CardContent>
              </form>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};

export default ProfileDetails;
